/** @format */

import {
	Chain,
	ProductPrice,
	ProductSkuPrices,
	ProductStylePrices,
	SkuAttribute
} from '@shared/data-access/interfaces';
import { Component, computed, inject, input, Signal } from '@angular/core';
import { getPrice, getProductPriceFromSku, getProductPriceFromStyle } from '@pdp/utils';
import {
	selectHasBothEmptyAndNonEmptyPrice,
	selectKillswitch,
	selectResolvedSku
} from '@pdp/data-access/state';

import { IntermediaryProductPrice } from '@pdp/data-access/interfaces';
import { ProductSelectionService } from '@pdp/data-access/services';
import { Store } from '@ngrx/store';
import { oosPrice } from './price-constants';
import { selectSelectedColorAttribute } from '@pdp/attributes/data-access';
import { CommonModule, CurrencyPipe, NgClass } from '@angular/common';
import { ToolTipModule } from '@shared/components';

@Component({
	selector: 'pdp-price',
	templateUrl: './price.component.html',
	styleUrl: './price.component.scss',
	imports: [CommonModule, NgClass, CurrencyPipe, ToolTipModule]
})
export class PriceComponent {
	readonly chain = inject(Chain);
	private readonly productSelectionService = inject(ProductSelectionService);
	private readonly store = inject(Store);

	readonly myCustomizerTotalPrice = input<number>();
	readonly index = input<number>();
	readonly selectedBucketIndex = input<number>();
	readonly bucket = input<SkuAttribute>();
	readonly bundleProductPrice = input<ProductStylePrices>();
	readonly bundleOverallPrice = input<ProductPrice>();
	readonly updatePriceBasedOnSelectedColor = input<boolean>(false);
	readonly isHybridPLA = input<boolean>(false);
	readonly ecodePriceData = input<ProductStylePrices>(
		this.productSelectionService.getProductStyleData()?.prices
	);
	readonly staticSkuPriceData = input<ProductSkuPrices>();

	isEnhancedSalesPriceEnable = this.store.selectSignal(
		selectKillswitch('isEnhancedSalesPriceEnable')
	);

	readonly resolvedSku = this.store.selectSignal(selectResolvedSku);
	readonly selectedColorAttribute = this.store.selectSignal(selectSelectedColorAttribute);
	readonly hasBothEmptyAndNonEmptyPrice = this.store.selectSignal(
		selectHasBothEmptyAndNonEmptyPrice
	);

	readonly skuPriceData = computed(() => {
		const staticSkuPriceData = this.staticSkuPriceData();
		const resolvedSkuPriceData = this.resolvedSku()?.prices;
		return staticSkuPriceData || resolvedSkuPriceData;
	});

	readonly productPrice: Signal<ProductPrice> = computed(() => {
		const skuPriceData = this.skuPriceData();
		const ecodePriceData = this.ecodePriceData();
		const selectedColor = this.selectedColorAttribute();
		const updateBasedOnSelectedColor = this.updatePriceBasedOnSelectedColor();
		const bucket = this.bucket();
		const bundleProductPrice = this.bundleProductPrice();
		const bundleOverallPrice = this.bundleOverallPrice();

		if (skuPriceData) {
			if (this.index() === this.selectedBucketIndex()) {
				return getProductPriceFromSku(skuPriceData);
			} else {
				return this.setBucketProductPriceData(this.bucket().productPrice);
			}
		} else if (selectedColor && updateBasedOnSelectedColor) {
			return selectedColor.prices;
		} else if (bucket) {
			return this.setBucketProductPriceData(bucket.productPrice);
		} else if (bundleProductPrice) {
			return getProductPriceFromStyle(bundleProductPrice);
		} else if (bundleOverallPrice) {
			return bundleOverallPrice;
		} else {
			return ecodePriceData ? getProductPriceFromStyle(ecodePriceData) : oosPrice;
		}
	});

	readonly showSelectOptionsText = computed(() => {
		const hasBothEmptyAndNonEmptyPrice = this.hasBothEmptyAndNonEmptyPrice();
		const skuPriceData = this.skuPriceData();
		const bucket = this.bucket();
		const isHybridPLA = this.isHybridPLA();
		return hasBothEmptyAndNonEmptyPrice && !skuPriceData && !bucket && !isHybridPLA;
	});

	readonly priceTextSizeClass = computed(() =>
		this.isHybridPLA() ? 'hmf-body-bold-l hmf-header-bold-md-m' : 'hmf-header-bold-m'
	);

	setBucketProductPriceData(price: ProductPrice) {
		const convertedPrice: IntermediaryProductPrice = {
			mapPriceIndicator: price.mapPriceIndicator,
			maxlistprice: Number(price.maxListPrice),
			maxofferprice: Number(price.maxOfferPrice),
			minlistprice: Number(price.minListPrice),
			minofferprice: Number(price.minOfferPrice),
			dealsPercentage: null,
			priceIndicator: price.priceIndicator
		};
		return getPrice(convertedPrice, convertedPrice.mapPriceIndicator === 1);
	}
}
